import { IContextualMenuItem, PersonaSize } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactNode, Ref, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { SafeExternalLink } from '@shared/lib/ui/components-alpha/SafeExternalLink';

import { IComponent, IconName } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { BasicList, IBasicListProps } from '../BasicList/BasicList';
import { basicListStylesNowrap } from '../BasicList/BasicList.styles';
import { Button, IButtonProps } from '../Button/Button';
import { buttonStylesIcon, buttonStylesMenu, buttonStylesMenuIcon } from '../Button/Button.styles';
import { IImageProps, Image } from '../Image/Image';
import { imageStylesLogo } from '../Image/Image.styles';
import { Item } from '../Item/Item';
import { itemStylesNav } from '../Item/Item.styles';
import { IPersonaProps } from '../Persona/Persona';
import { Persona } from '../Persona/Persona';
import { personaStylesMenuButton } from '../Persona/Persona.styles';
import { appBarStylesDefault, IAppBarClassNames, IAppBarStyles } from './AppBar.styles';

export interface MenuItem {
  key: string;
  dataTest?: string;
  name: string;
  to: string;
  isExternal?: boolean;
  target?: string;
  rel?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, item: MenuItem) => void;
}

export interface IAppBarProps extends IComponent<IAppBarStyles>, HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
  menuItems?: MenuItem[];
  userMenuItems?: IContextualMenuItem[];
  menuButtonProps?: IButtonProps;
  menuIconButtonProps?: IButtonProps;
  personaProps?: IPersonaProps;
  logoImageProps?: IImageProps;
  hasMenuIcon?: boolean;
  title?: string;
  selectedMenuItemKey?: string;
  selectableExternalLink?: boolean;
  onMenuIconClick?: () => void;
  onLogoClick?: () => void;
  onTitleClick?: () => void;
  onMenuItemClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, menu: MenuItem) => void;
}

export const AppBar = forwardRef((props: IAppBarProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    children,
    menuItems,
    userMenuItems,
    menuButtonProps = {},
    menuIconButtonProps = {},
    personaProps,
    logoImageProps,
    hasMenuIcon,
    title,
    styles,
    selectedMenuItemKey,
    selectableExternalLink,
    onMenuIconClick,
    onLogoClick,
    onTitleClick,
    onMenuItemClick,
    ...elementProps
  } = props;
  const [selectedMenuKey, setSelectedMenuKey] = useState(selectedMenuItemKey);
  const [isMenuShow, setIsMenuShow] = useState(false);
  useEffect(() => {
    if (selectedMenuItemKey !== selectedMenuKey) setSelectedMenuKey(selectedMenuItemKey);
    // Intentionally omitting selectedMenuKey from dependency list since we dont want to undo internal state changes
  }, [selectedMenuItemKey]);
  const classNames = useClassNames<IAppBarStyles, IAppBarClassNames>('AppBar', appBarStylesDefault, styles, className);
  let _personaProps: IPersonaProps;
  if (personaProps) {
    const { size = PersonaSize.size40, styles = personaStylesMenuButton } = personaProps;
    _personaProps = { ...personaProps, styles, size };
  }
  const {
    styles: mibStyles,
    iconName: mibIconName = IconName.GlobalNavButton,
    title: mibTitle = 'Menu',
    onClick: mibOnClick = () => {
      setIsMenuShow(!isMenuShow);
      if (onMenuIconClick) {
        onMenuIconClick();
      }
    },
  } = menuIconButtonProps;
  const mibProps: IButtonProps = {
    ...menuIconButtonProps,
    styles: mibStyles || [buttonStylesIcon, buttonStylesMenuIcon],
    iconName: mibIconName,
    title: mibTitle,
    onClick: mibOnClick,
  };
  const {
    styles: mbStyles,
    menuItems: mbMenuItems = userMenuItems,
    onRenderText: mbLabelRenderer = () => <Persona key="app-bar-user-menu-persona" {..._personaProps} />,
  } = menuButtonProps;
  const mbProps: IButtonProps = {
    ...menuButtonProps,
    styles: mbStyles || [buttonStylesMenu],
    menuItems: mbMenuItems,
    onRenderText: mbLabelRenderer,
  };
  const menuListProps: IBasicListProps<MenuItem> | undefined = menuItems
    ? {
        styles: basicListStylesNowrap,
        idField: 'to',
        horizontal: true,
        data: menuItems,
        itemRenderer: (d) => {
          const { key, to, name, dataTest, isExternal, target, rel, onClick } = d;
          const linkProps = {
            'data-test': dataTest,
            target,
            rel,
            className: key === selectedMenuKey ? 'selected' : undefined,
            onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
              setIsMenuShow(false);
              if (isExternal) {
                if (selectableExternalLink) {
                  setSelectedMenuKey(key);
                }
              } else {
                setSelectedMenuKey(key);
              }
              if (onMenuItemClick) {
                onMenuItemClick(e, d);
              }
              if (onClick) {
                onClick(e, d);
              }
            },
          };
          return (
            <Item
              idField="to"
              styles={itemStylesNav}
              data={d}
              renderer={() =>
                // <NavLink to={{pathname: 'external link'}}> doesn't work in react-router-dom v6.
                isExternal ? (
                  <SafeExternalLink {...linkProps} href={to}>
                    {name}
                  </SafeExternalLink>
                ) : (
                  <NavLink {...linkProps} to={to}>
                    {name}
                  </NavLink>
                )
              }
            />
          );
        },
      }
    : undefined;
  return (
    <div
      {...elementProps}
      ref={ref}
      className={`${classNames.root} ${isMenuShow ? 'main-nav-show' : ''}`}
      data-test={props['data-test'] || 'app-bar'}
    >
      {logoImageProps ? (
        <div className={classNames.logo} onClick={() => onLogoClick!()}>
          <Image {...logoImageProps} styles={logoImageProps?.styles || imageStylesLogo} />
        </div>
      ) : (
        ''
      )}
      {hasMenuIcon ? (
        <div className={classNames.menuIcon}>
          <Button {...mibProps} />
        </div>
      ) : (
        ''
      )}
      {title ? (
        <div className={classNames.title} onClick={() => onTitleClick!()}>
          {title}
        </div>
      ) : (
        ''
      )}
      <div className={classNames.content}>
        {menuItems ? (
          <div className={classNames.contentContainer}>
            <div className={classNames.menu}>
              <BasicList data-test="app-bar-basic-list" {...menuListProps} />
            </div>
            <div className={classNames.children}>
              <div className={classNames.childrenContent}>{children}</div>
            </div>
          </div>
        ) : (
          <div className={classNames.children}>
            <div className={classNames.childrenContent}>{children}</div>
          </div>
        )}
      </div>
      {personaProps && (
        <div className={classNames.userMenu}>
          <Button data-test="app-bar-user-menu-button" {...mbProps} />
        </div>
      )}
    </div>
  );
});
